import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  useTheme,
} from '@mui/material';
import {
  Home,
  ReceiptLong,
  PlaylistAdd,
  AccountBalanceWallet,
  Analytics,
  Settings,
  HelpCenter,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  authRequired?: boolean;
}

/**
 * Navigation items aligned to canonical routes and new implementations
 */
const navigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: <Home />,
    authRequired: false,
  },
  {
    // Represents the New Transactions implementation, exposed as "Transactions"
    label: 'Transactions',
    path: '/transactions',
    icon: <ReceiptLong />,
    authRequired: true,
  },
  {
    // Represents the New Categories implementation, exposed as "Categories"
    label: 'Categories',
    path: '/categories',
    icon: <PlaylistAdd />,
    authRequired: true,
  },
  {
    // Represents the New Accounts implementation, exposed as "Accounts"
    label: 'Accounts',
    path: '/accounts',
    icon: <AccountBalanceWallet />,
    authRequired: true,
  },
  {
    label: 'Analytics',
    path: '/analytics',
    icon: <Analytics />,
    authRequired: true,
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: <Settings />,
    authRequired: true,
  },
  {
    label: 'About',
    path: '/about',
    icon: <HelpCenter />,
    authRequired: true,
  },
];

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  width?: number;
  variant?: 'persistent' | 'temporary';
}

/**
 * Application drawer component for navigation
 */
const AppDrawer: React.FC<AppDrawerProps> = ({
  open,
  onClose,
  width = 240,
  variant = 'persistent',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  const handleNavigation = (path: string, disabled: boolean) => {
    if (disabled) {
      return; // prevent navigation when disabled
    }
    navigate(path);
    // Close temporary drawer after navigation
    if (variant === 'temporary') {
      onClose();
    }
  };

  const isActivePath = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant={variant}
      anchor='left'
      open={open}
      onClose={onClose}
      sx={{
        // Do not set width on the root; this avoids reserving space when closed
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        <List>
          {navigationItems.map(item => {
            const isActive = isActivePath(item.path);
            const isDisabled = Boolean(item.authRequired && !isAuthenticated);

            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path, isDisabled)}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  selected={isActive}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'initial',
                    px: 2.5,
                    ...(isDisabled && { pointerEvents: 'none' }),
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: 'center',
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

export default AppDrawer;
